/**
 * Single source of truth for companion-banner networks (Spotify, SiriusXM, Adswizz, ...).
 *
 * Every surface that renders or validates companion-banner networks (the per-creative
 * validation table, the two creative-library listing screens, and the CSV exporters)
 * reads from COMPANION_BANNER_NETWORKS instead of hardcoding each network. Adding a
 * network is a single entry here.
 *
 * Each network carries its own id per surface (rather than deriving all of them from
 * `key`) because Spotify/SiriusXM already have three different id-casing conventions
 * baked into existing testids, enum values, and CSV headers — preserving those exact
 * strings keeps this refactor behavior-invisible for the two existing networks.
 */
export interface CompanionBannerNetworkConfig {
  /** Internal id — used as the key in a validation row's `results` map. */
  key: string;
  /** Display label — detail-table column header and CSV header. */
  label: string;
  /** CompanionBannerValidationTable column key; drives the `cell-<id>` testid. */
  detailTableColumnId: string;
  /** Column id for the modern (frontier/lib) creative-library listing screen. */
  frontierListingColumnId: string;
  /** Column label for the modern creative-library listing screen. */
  frontierListingLabel: string;
  /** Column id for the legacy ExistingCreativesScreen listing screen. */
  existingScreenColumnId: string;
  /** Allowed "WxH" dimension strings. */
  dimensions: string[];
  /** Allowed mime type strings, e.g. "image/jpeg". */
  fileTypes: string[];
  maxFileSizeBytes: number;
  /** Overrides the auto-generated "File type must be X or Y" message when set. */
  fileTypeMessage?: string;
}

export const COMPANION_BANNER_NETWORKS: CompanionBannerNetworkConfig[] = [
  {
    key: "spotify",
    label: "Spotify Validations",
    detailTableColumnId: "spotify",
    frontierListingColumnId: "spotifyStandards",
    frontierListingLabel: "Spotify Banner Standards",
    existingScreenColumnId: "spotify-banner-standards",
    dimensions: ["600x600", "300x250"],
    fileTypes: ["image/jpeg", "image/png"],
    maxFileSizeBytes: 204800
  },
  {
    key: "siriusXM",
    label: "Sirius XM/Pandora Validations",
    detailTableColumnId: "sirius_xm",
    frontierListingColumnId: "siriusXmStandards",
    frontierListingLabel: "Sirius XM Banner Standards",
    existingScreenColumnId: "sirius-xm-banner-standards",
    dimensions: ["300x250", "300x600"],
    fileTypes: ["image/jpeg"],
    fileTypeMessage: "File type must be JPEG only (PNG not supported)",
    maxFileSizeBytes: 102400
  },
  {
    key: "adswizz",
    label: "Adswizz Validations",
    detailTableColumnId: "adswizz",
    frontierListingColumnId: "adswizzStandards",
    frontierListingLabel: "Adswizz Banner Standards",
    existingScreenColumnId: "adswizz-banner-standards",
    dimensions: ["300x250", "728x90"],
    fileTypes: ["image/jpeg", "image/png", "image/gif"],
    maxFileSizeBytes: 2 * 1024 * 1024
  }
];
